import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { CustomThemeContext } from 'common/providers/CustomThemeProvider'
import i18n from 'lang/idiomas'
import { useContext, useState } from 'react'
import ListaSolicitudes from '../components/ListaSolicitudes'
import SolicitudesPendientes from '../components/SolicitudesPendientes'
import useFetchEsValidador from '../hooks/useFetchEsValidador'
import useFetchSolicitudesPendientes from '../hooks/useFetchSolicitudesPendientes'

const sectionWrapperSx = {
  my: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
}

const sectionLabelSx = {
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 500,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  lineHeight: 1,
}

const tabLabelSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
}

const tabsSx = {
  borderBottom: 1,
  borderColor: 'divider',
  mb: 2,
}

const SolicitudesUsuario = ({ object }) => {
  const { themeController } = useContext(CustomThemeContext)
  const { esValidador, loading: loadingValidador } = useFetchEsValidador()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))

  const ocultarPendientes = themeController?.hide?.Solicitudes?.pendientes_validacion === true
  const mostrarPendientes = !loadingValidador && esValidador && !ocultarPendientes

  const { response: solicitudesPendientes, loading: loadingPendientes } =
    useFetchSolicitudesPendientes({ enabled: mostrarPendientes })

  let listaProps = {}
  if (object) {
    const datos = object.split('|')
    listaProps = { tipo: datos[0], subtipo: datos[1] }
  }

  const numPendientes = solicitudesPendientes?.length ?? 0
  const usarTabs = isMobile && mostrarPendientes
  const [tab, setTab] = useState(object ? 'disponibles' : 'pendientes')

  const headerDisponibles = (
    <Box sx={sectionWrapperSx}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <AddCircleOutlineRoundedIcon color="primary" fontSize="small" />
        <Typography variant="overline" sx={sectionLabelSx}>
          {i18n.t('solicitudes.disponibles', { defaultValue: 'Solicitudes disponibles' })}
        </Typography>
      </Stack>
      <Divider sx={{ flexGrow: 1 }} />
    </Box>
  )

  const headerPendientes = (
    <Box sx={sectionWrapperSx}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <GavelRoundedIcon color="primary" fontSize="small" />
        <Typography variant="overline" sx={sectionLabelSx}>
          {i18n.t('solicitudes.pendientes_validacion', {
            defaultValue: 'Pendientes de validación',
          })}
        </Typography>
        <Chip size="small" color="warning" variant="outlined" label={numPendientes} />
      </Stack>
      <Divider sx={{ flexGrow: 1 }} />
    </Box>
  )

  if (usarTabs) {
    return (
      <>
        <Tabs
          value={tab}
          onChange={(_event, value) => setTab(value)}
          variant="fullWidth"
          sx={tabsSx}
        >
          <Tab
            value="disponibles"
            label={i18n.t('solicitudes.disponibles', {
              defaultValue: 'Solicitudes disponibles',
            })}
          />
          <Tab
            value="pendientes"
            label={
              <Box component="span" sx={tabLabelSx}>
                {i18n.t('solicitudes.pendientes_validacion', {
                  defaultValue: 'Pendientes de validación',
                })}
                <Chip size="small" color="warning" label={numPendientes} />
              </Box>
            }
          />
        </Tabs>

        <Box
          role="tabpanel"
          hidden={tab !== 'disponibles'}
          sx={{ display: tab === 'disponibles' ? 'block' : 'none' }}
        >
          <ListaSolicitudes {...listaProps} lista />
        </Box>
        <Box
          role="tabpanel"
          hidden={tab !== 'pendientes'}
          sx={{ display: tab === 'pendientes' ? 'block' : 'none' }}
        >
          <SolicitudesPendientes data={solicitudesPendientes} loading={loadingPendientes} />
        </Box>
      </>
    )
  }

  return (
    <>
      {headerDisponibles}
      <ListaSolicitudes {...listaProps} lista />

      {mostrarPendientes && (
        <>
          {headerPendientes}
          <SolicitudesPendientes data={solicitudesPendientes} loading={loadingPendientes} />
        </>
      )}
    </>
  )
}

export default SolicitudesUsuario
